import Component from '@glimmer/component'
import { service } from '@ember/service'
import { fn } from '@ember/helper'
import { on } from '@ember/modifier'
import { not } from 'ember-truth-helpers'
import type InteractivePreviewService from 'teacher-ui-client/services/interactive-preview'
import { InteractiveModal } from 'teacher-ui-client/components/interactive-modal'
import FidgetSpinnerWaveComponent from '@blakeelearning/fidget/components/fidget/spinner/wave'
import ContentInteractive from '@blakeelearning/content-loader/modifiers/content-interactive'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { eventValue } from 'teacher-ui-client/helpers/event-value'
import { BaseSelect } from 'teacher-ui-client/components/base/select'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { modifier } from 'ember-modifier'
import type { Log } from '@blakeelearning/log'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'

interface ContentLoadingSignature {
  Element: HTMLElement
  Args: {
    Named: Record<string, unknown>
    Positional: [manifest: unknown]
  }
}

/**
 * A modifier to show an element while content is loading, and hide when it is done.
 *
 * It read the LOAD_COMPLETE event dispatched by caper. It also works for jester, as we provide a readyForUserInput
 * function in the jester externalController to dispatch the same event.
 *
 * The manifest is provided (but unused) to ensure the modifier re-runs each time the manifest changes.
 */
const contentLoading = modifier<ContentLoadingSignature>(function (element, [_manifest]) {
  element.style.display = 'block'

  const doneLoading = () => (element.style.display = 'none')

  document.addEventListener('LOAD_COMPLETE', doneLoading, false)
  return () => {
    document.removeEventListener('LOAD_COMPLETE', doneLoading, false)
  }
})

interface HandleMeJsVideoSignature {
  Element: HTMLElement
  Args: {
    Named: Record<string, unknown>
    Positional: [manifest: unknown]
  }
}

/**
 * This modifier handles some quirks for interactives that render videos with mediaelement.js - specifically MS and REX.
 *
 * It observes child elements added to the DOM, if video element found it ensures it has controls attribute (for native
 * html video controls)
 *
 * mediaelement.js is seems to be a custom JS video library that renders its own controls, but it requires a bunch of
 * awful CSS to be imported. We can just use native controls.
 *
 * I'm not sure why mejs is used, but I assume very legacy, to support IE11 and other browsers that don't have <video>
 * or it's to apply kid-friendly video styles.
 */
const handleMeJsVideo = modifier<HandleMeJsVideoSignature>(function (element, [_manifest]) {
  // hold reference for cleanup
  let videoElement: HTMLVideoElement | null | undefined
  let setup = false

  const observer = new MutationObserver((mutationList) => {
    mutationList.forEach((mutation) => {
      if (mutation.addedNodes && element && !setup) {
        const containerElement: HTMLDivElement | null = element.querySelector('div.mejs-video')
        videoElement = containerElement?.querySelector('video')
        if (containerElement && videoElement) {
          setup = true

          // turn native controls on
          videoElement.setAttribute('controls', 'true')

          /* For some reason, mediaelement.js video with native controls in Safari fires off a pause event every time
           * the user hits play, and vice versa. This code detects if a play event is immediately followed by a pause,
           * if so it plays the video again (and the same in the other direction).
           *
           * While only needed for Safari, this should be harmless to include for chrome/firefox even though they work
           * flawlessly.
           */
          const blockOppositeEvent = () => {
            if (!videoElement) return
            videoElement.addEventListener('play', () => {
              const ignorePause = (ev: Event) => {
                ev.stopPropagation()
                if (videoElement?.paused === true) void videoElement.play()
              }
              videoElement?.addEventListener('pause', ignorePause, { capture: true })
              setTimeout(() => videoElement?.removeEventListener('pause', ignorePause, { capture: true }), 200)
            })
            videoElement.addEventListener('pause', () => {
              const ignorePlay = (ev: Event) => {
                ev.stopPropagation()
                if (videoElement?.paused === false) videoElement.pause()
              }
              videoElement?.addEventListener('play', ignorePlay, { capture: true })
              setTimeout(() => videoElement?.removeEventListener('play', ignorePlay, { capture: true }), 200)
            })
          }

          videoElement.addEventListener('canplay', blockOppositeEvent, false)
        }
      }
    })
  })

  observer.observe(element, { childList: true, subtree: true })
  return () => {
    observer.disconnect()
    // Safari has issues where it continues playing video after video element removed from DOM. Workaround is to remove
    // its source attribute.
    videoElement?.setAttribute('src', '')
  }
})

/**
 * https://3plearning.atlassian.net/wiki/spaces/TY/pages/3811278862/Interactive+Preview+Engines+in+TUI
 */
export class InteractivePreview extends Component<unknown> {
  @service interactivePreview!: InteractivePreviewService

  @service subscriptionType!: SubscriptionTypeService

  @service log!: Log

  abortController = new AbortController()

  get signal() {
    return this.abortController.signal
  }

  override willDestroy(): void {
    // Abort loading/rendering the interactive when the component is destoryed
    this.abortController.abort('Destroying interactive')
    super.willDestroy()
  }

  /**
   * Handing each interactive a clean variable object, in case they mutate it, to avoid that carrying over to the next
   * interactive that is previewed.
   */
  get variables() {
    return this.interactivePreview.selectedPreview?.variables ?? {}
  }

  onError = (error: unknown) => {
    // Check that any error may be the result of aborting the interactive
    if (this.signal.aborted) {
      // This is the "reason" from the willDestry method, just ignore it
    } else {
      // Applications should handle any other errors
      this.log.error('error callback of content-interactive', error ?? undefined)
    }
  }

  <template>
    {{#if this.interactivePreview.selectedPreview}}
      {{! In "modal" mode, the modal has no set width or height, it is up to the content }}
      {{! In "fullscreen" mode, the modal is inset-0, so has a fixed width & height }}
      <InteractiveModal
        @isFullscreen={{this.interactivePreview.isFullscreen}}
        @onClose={{this.interactivePreview.closePreview}}
        @showBackdrop={{not this.interactivePreview.isFullscreen}}
      >
        {{! Use the full height provided by the modal in fullscreen mode }}
        <div data-test-preview-container class="flex flex-col {{if this.interactivePreview.isFullscreen 'h-full'}}">
          {{! ensure header is exact same width as interactive when in modal view }}
          <div
            class="flex gap-4 p-4
              {{unless this.interactivePreview.isFullscreen 'interactive-preview-width-16/9' 'w-full'}}"
          >
            <div class="flex flex-wrap items-center gap-4">
              {{#if this.interactivePreview.options.title}}<h3>{{this.interactivePreview.options.title}}</h3>{{/if}}
              {{#if this.interactivePreview.selectedPreview.navigation}}
                <BaseSelect
                  @themeKey={{themeKey this.subscriptionType.current}}
                  {{on "input" (eventValue this.interactivePreview.selectedPreview.goToPart)}}
                >
                  {{#each this.interactivePreview.selectedPreview.navigation as |nav|}}
                    <option value={{nav.id}} selected={{nav.current}}>
                      {{! template-lint-disable no-bare-strings}}
                      Part
                      {{nav.label}}
                    </option>
                  {{/each}}
                </BaseSelect>
              {{/if}}
            </div>
            <div class="ml-auto shrink-0">
              <UiButton
                @appearance={{ButtonAppearance.regular}}
                {{on "click" (fn this.interactivePreview.setFullscreen (not this.interactivePreview.isFullscreen))}}
              >
                <PhosphorIcon @icon="phosphor-arrows-out-simple-fill" />
              </UiButton>
              <UiButton @appearance={{ButtonAppearance.regular}} {{on "click" this.interactivePreview.closePreview}}>
                <PhosphorIcon @icon="phosphor-x-bold" />
              </UiButton>
            </div>
          </div>
          {{! This log is here intentionally for now, else previews don't load locally under ember-source 5.6+ }}
          {{! Can be removed when this is resolved - https://github.com/universal-ember/reactiveweb/issues/110 }}
          {{! template-lint-disable no-log }}
          {{log this.interactivePreview.selectedPreview.currentManifest}}
          {{! Shown on top of the preview area below - while retrieving manifest, and also while the activity is
          downloading its dependencies }}
          <div
            data-test-preview-loading
            class="absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2 transform"
            {{contentLoading this.interactivePreview.selectedPreview.currentManifest}}
          >
            <FidgetSpinnerWaveComponent @centered={{true}} />
          </div>
          {{!
          Modal mode: We give a fixed size to the element, and the activity determines how it will best fit in the
          element. By making the element the exact desired aspect ratio of the activity (eg 16/9), the modal should
          hug the interactive with no extra space around it.

          Fullscreen mode:
          Being a div/block element, the width is already 100%, and the flex-col parent provides the available height,
          just need to ensure the element adheres to that height with grow/shink/basis classes.

          The relative is needed, as some video interactives (eg RE reading lessons) have a Skip Video button that
          seems to render absolutely

          The flex centering just ensures if the activity decides to take up less room than it is given, it will be
          centered within that box. Mostly relevant for fullscreen mode.
          }}
          {{! As of writing, conditional modifiers don't seem to call their destroy functions - so this is implemented
          instead with an if block, by rendering an identical element with & without the modifier.
          See: https://github.com/ember-modifier/ember-modifier/issues/613
          }}
          {{#if this.interactivePreview.selectedPreview.currentManifest}}
            <div
              data-test-preview-activity
              class="relative flex items-center justify-center
                {{if
                  this.interactivePreview.isFullscreen
                  'min-h-0 shrink grow basis-0'
                  'interactive-preview-width-16/9 interactive-preview-height-16/9'
                }}"
              {{handleMeJsVideo this.interactivePreview.selectedPreview.currentManifest}}
              {{ContentInteractive
                this.interactivePreview.selectedPreview.loader
                externalController=this.interactivePreview.selectedPreview.externalController
                manifests=this.interactivePreview.selectedPreview.currentManifest
                variables=this.variables
                onError=this.onError
                signal=this.signal
              }}
            ></div>
          {{else}}
            {{! Same as above just without the modifiers }}
            <div
              data-test-preview-manifest-loading
              class="relative flex items-center justify-center
                {{if
                  this.interactivePreview.isFullscreen
                  'min-h-0 shrink grow basis-0'
                  'interactive-preview-width-16/9 interactive-preview-height-16/9'
                }}"
            />
          {{/if}}
        </div>
      </InteractiveModal>
    {{/if}}
  </template>
}

export default InteractivePreview
